import { type JewelSocketProfile, SAW_JEWEL_ID, toExactBigInt } from '@kenocyber/shared';
import { BPS_ONE } from './exact';
import type { DamageOrigin } from './origin';
import { PROC_BUDGET_BPS_PER_LEVEL, PROC_LEVEL_CAP } from './proc-lottery';

/**
 * **O Diamante Serrilhado: serras que ORBITAM o casco, e nunca um proc.**
 *
 * ## O que ele é, e porque não entra em loteria nenhuma
 *
 * As sete joias que já existem esperam por um acontecimento. O Ciclone corta no
 * acerto que o trouxe, a Bomba e o Arsenal disputam a salva, o Orbe da Víbora e
 * a Garra semeiam estado num acerto direto, o Orbe Vampírico devolve o que um
 * golpe removeu, e a Égide responde a um payload que já vinha a caminho. As sete
 * têm um GATILHO.
 *
 * Esta não tem. As serras existem enquanto a nave existir: nascem quando ela
 * nasce, giram enquanto ela vive e param quando ela morre. Não dependem de
 * acertar um tiro, não substituem um tiro, não são projéteis e não têm chance
 * nenhuma a sortear - **nada neste ficheiro consome entropia**, e é por isso que
 * ele não tem seletor. Os três procs que um contacto confirmado pode semear
 * sorteiam, mas quem os sorteia são as simulações, de um fluxo próprio das
 * serras ({@link PVP_SAW_RNG_LABEL}, {@link HUNT_SAW_RNG_LABEL}) que existe
 * precisamente para não deslocar as sequências que já existiam.
 *
 * O que ele tem em vez de uma chance é um RELÓGIO por par serra→alvo: enquanto
 * a serra estiver a tocar o inimigo, ela cobra uma instância de dano de cada vez
 * que o intervalo dela vence. Uma serra não pode doer a cada tique, e o que a
 * impede não é uma verificação escrita numa simulação - é a aritmética de
 * {@link sawContactDue}, que as duas usam.
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não conhece um alvo, uma arena, um casco, um mob nem um boss. Não desenha, não
 * sabe que uma serra é preta e não sabe quantos dentes ela tem. Não lê o relógio
 * e não sorteia. Recebe números e devolve números - as duas simulações trazem o
 * seu próprio vocabulário e a sua própria unidade de tempo, e é isso que faz a
 * caça e o duelo partilharem esta lei em vez de a copiarem.
 */

/* -------------------------------------------------------------------------- */
/*  A quantidade                                                              */
/* -------------------------------------------------------------------------- */

/** O nível a que tudo nesta joia deixa de crescer. O mesmo teto das outras. */
export const SAW_LEVEL_CAP = PROC_LEVEL_CAP;

/** De quantos em quantos níveis nasce uma serra nova. */
export const SAW_LEVELS_PER_BLADE = 10;

/**
 * **Quantas serras, no máximo.** Quatro, e é DERIVADO em vez de escrito.
 *
 * `1 + floor(30 / 10)`. Escrever quatro à mão seria um número que deixa de estar
 * certo no dia em que o teto ou o degrau mudarem, e que ninguém iria procurar.
 */
export const SAW_MAX_BLADES = 1 + Math.floor(SAW_LEVEL_CAP / SAW_LEVELS_PER_BLADE);

/**
 * O nível que conta, com o teto aplicado. **Defesa, nunca permissão.**
 *
 * Um perfil com 31 é recusado por todas as fronteiras que o escrevem - base,
 * socket, API, bilhete, instantânea e perfil - e se algum dia um chegar aqui, o
 * que ele não vai é comprar poder. Abaixo de 1 devolve 0, que é "sem joia".
 */
function effectiveLevel(level: number): number {
  if (!Number.isFinite(level) || level <= 0) {
    return 0;
  }
  const whole = Math.floor(level);
  return whole > SAW_LEVEL_CAP ? SAW_LEVEL_CAP : whole;
}

/**
 * **Quantas serras este nível mantém.**
 *
 *     sawCount = 1 + floor(min(nível, 30) / 10)
 *
 * | nível | serras |
 * | --- | --- |
 * | 1–9 | 1 |
 * | 10–19 | 2 |
 * | 20–29 | 3 |
 * | 30 | 4 |
 * | 31 e acima | 4 |
 *
 * Sem a joia são **zero**, e é {@link sawBladeCount} que responde a essa
 * pergunta: esta função recebe um nível, e um nível zero já não existe.
 */
export function sawBladeCountForLevel(level: number): number {
  const effective = effectiveLevel(level);
  if (effective <= 0) {
    return 0;
  }
  return 1 + Math.floor(effective / SAW_LEVELS_PER_BLADE);
}

/** O nível do Diamante Serrilhado neste casco, ou 0 se ele não o tem. */
export function sawLevelOf(profile: JewelSocketProfile): number {
  const owned = profile.entries.find((entry) => entry.id === SAW_JEWEL_ID);
  return owned === undefined ? 0 : effectiveLevel(owned.levelSum);
}

/**
 * **Quantas serras este casco tem.** Zero sem a joia, e a saída é antecipada.
 *
 * A saída antecipada não é uma otimização: é o que faz uma partida sem esta joia
 * produzir exatamente o estado que produzia antes deste gate, restos incluídos.
 */
export function sawBladeCount(profile: JewelSocketProfile): number {
  return sawBladeCountForLevel(sawLevelOf(profile));
}

/* -------------------------------------------------------------------------- */
/*  A velocidade                                                              */
/* -------------------------------------------------------------------------- */

/**
 * O denominador da velocidade: **29**, e é `teto - 1`.
 *
 * A progressão é linear do nível 1 ao 30, portanto ela tem 29 degraus e não 30.
 * Derivado do teto pela mesma razão que {@link SAW_MAX_BLADES} é.
 */
export const SAW_SPEED_SCALE = SAW_LEVEL_CAP - 1;

/**
 * **O numerador da velocidade deste nível.** A progressão inteira, sem float.
 *
 *     multiplicador = (28 + min(nível, 30)) / 29
 *
 * | nível | fração | multiplicador |
 * | --- | --- | --- |
 * | 1 | 29/29 | 1,0000x |
 * | 9 | 37/29 | 1,2759x |
 * | 10 | 38/29 | 1,3103x |
 * | 19 | 47/29 | 1,6207x |
 * | 20 | 48/29 | 1,6552x |
 * | 29 | 57/29 | 1,9655x |
 * | 30 | 58/29 | **2,0000x**, e é onde para |
 *
 * ## Porque uma FRAÇÃO e não um multiplicador em milésimos
 *
 * Porque `1000 x (28 + L) / 29` não é inteiro para vinte e oito dos trinta
 * níveis - `29` é primo e nunca divide - e arredondá-lo faria a rotação
 * desenhada e a cadência de dano derivarem de dois números que já não são o
 * mesmo. O contrato exige que os dois saiam do MESMO multiplicador, e uma fração
 * exata é a única forma de o cumprir sem relógios independentes.
 *
 * Sem a joia devolve zero, e zero não é uma velocidade: é a ausência de serras.
 */
export function sawSpeedNumerator(level: number): number {
  const effective = effectiveLevel(level);
  if (effective <= 0) {
    return 0;
  }
  return SAW_SPEED_SCALE + effective - 1;
}

/* -------------------------------------------------------------------------- */
/*  O intervalo de contacto                                                   */
/* -------------------------------------------------------------------------- */

/**
 * **O intervalo histórico: 400 ms no nível 1.**
 *
 * É o número que o contrato V28D.2 já dava a esta joia, e ele sobrevive ao gate
 * V28G.P como REFERÊNCIA do nível 1. O que mudou não foi o intervalo do primeiro
 * nível - foi ele deixar de ser fixo.
 */
export const SAW_BASE_CONTACT_INTERVAL_MS = 400;

/**
 * O numerador do intervalo, em ms x {@link SAW_SPEED_SCALE}. **Derivado.**
 *
 *     intervalo(L) = 11 600 / (28 + min(L, 30)) ms
 *
 * | nível | intervalo exato | contactos por serra/s |
 * | --- | --- | --- |
 * | 1 | 400 ms | 2,5 |
 * | 9 | 313,51 ms | 3,19 |
 * | 10 | 305,26 ms | 3,28 |
 * | 19 | 246,81 ms | 4,05 |
 * | 20 | 241,67 ms | 4,14 |
 * | 29 | 203,51 ms | 4,91 |
 * | 30 | **200 ms** | 5,0 |
 *
 * Nunca dividido antes de ser comparado: `11600/37` é uma dízima infinita, e
 * decidir uma instância de dano pelo último bit de um `double` seria decidi-la
 * por qual motor a executou. {@link sawContactDue} multiplica em vez de dividir.
 */
export const SAW_CONTACT_INTERVAL_NUMERATOR_MS = SAW_BASE_CONTACT_INTERVAL_MS * SAW_SPEED_SCALE;

/**
 * **Se este par serra→alvo já pode voltar a doer.**
 *
 * `elapsedMs` é o tempo decorrido desde a última instância de dano DESTE par, no
 * relógio do modo já convertido a milissegundos. Os dois modos convertem porque
 * as unidades deles são diferentes - o duelo conta tiques de 50 ms e a caça
 * conta milissegundos de 16 em 16 - e um campo em "segundos" obrigaria um dos
 * dois a arredondar.
 *
 * ## Porque uma comparação e não um temporizador
 *
 * Porque um temporizador tem de ser decrementado por alguém, e quem o decrementa
 * tem de correr em todos os tiques - incluindo o tique em que a sala foi
 * reconstruída de uma instantânea. Isto é uma função do relógio e do estado
 * guardado, portanto uma retoma a meio de um cooldown continua exatamente onde
 * estava sem ninguém ter de se lembrar de nada.
 *
 * ## A quantização do modo, declarada em vez de escondida
 *
 * O tique é a granularidade real. No duelo, 50 ms: o nível 1 vence em 8 tiques
 * (400 ms exatos) e o nível 30 em 4 (200 ms exatos), e um nível intermédio
 * arredonda PARA CIMA - o nível 9 pede 313,51 ms e cobra a 350. Na caça, 16 ms:
 * o mesmo nível 9 cobra a 320. É o mesmo tipo de resíduo que a cadência de tiro
 * já tem, e compensá-lo com uma instância extra seria trocar décimos de
 * milissegundo por um contacto que deixa de ser determinístico.
 */
export function sawContactDue(elapsedMs: number, level: number): boolean {
  const speed = sawSpeedNumerator(level);
  if (speed <= 0) {
    return false;
  }
  return elapsedMs * speed >= SAW_CONTACT_INTERVAL_NUMERATOR_MS;
}

/* -------------------------------------------------------------------------- */
/*  A magnitude                                                               */
/* -------------------------------------------------------------------------- */

/**
 * **O orçamento: 240 bps do dano-base, por segundo, por nível.**
 *
 * ## De onde vem este número, e porque não é escolhido aqui
 *
 * Do orçamento declarado do V28D.2 - **um socket vale 30 bps de poder de combate
 * por nível** - convertido para a unidade que uma joia de TEMPORIZADOR precisa.
 * As outras sete são medidas por acerto ou por salva; esta é medida por segundo,
 * e a conversão é a do modelo que `docs/gates/v28d2-jewel-math.mjs` já usa:
 *
 *     240 = 30 bps/nível  x  2 acertos/s  /  0,25 de tempo em contacto
 *
 * Os dois parâmetros do meio são do MODELO e estão declarados lá: 2 acertos por
 * segundo é `5 tiros/s x 40 % de acerto`, e 0,25 é a fração do tempo em que um
 * inimigo está dentro do alcance das serras. Dividir pela segunda é o que
 * transforma "o que a joia rende em média" no que ela rende **sob contacto
 * sustentado**, que é a grandeza que este ficheiro produz.
 *
 * As duas consequências, que são o contrato:
 *
 * - nível 1 — `+0,30 %` do DPS realizado;
 * - nível 30 — `+9 %`, e é o teto final.
 *
 * O gerador do V28D.2 media 31,3 e 937,5 bps para esta joia com o intervalo fixo
 * de 400 ms e o coeficiente de 100 bps por pulso. Este número põe-a exatamente
 * em 30 e 900 - dentro da banda `900 +/- 250` como estava, e agora no centro dela.
 *
 * ## O que ele NÃO multiplica
 *
 * Nada além do dano-base autoritativo da nave. Não lê a Chama da Destruição, não
 * lê os Corações Primordiais, não lê a runa de Dano Dobrado, não lê crítico e
 * não lê outro proc - a mesma leitura que todas as outras joias fazem.
 */
export const SAW_BUDGET_BPS_PER_LEVEL_PER_SECOND =
  Number(PROC_BUDGET_BPS_PER_LEVEL) * 2 * (1 / 0.25);

/** Milissegundos num segundo, nomeado uma vez para a fração abaixo se ler. */
const MS_PER_SECOND = 1000n;

/**
 * **O denominador de uma instância de dano.** Exportado para o carry o partilhar.
 *
 * `serras x velocidade x 1000 x 10 000`. É o que transforma o orçamento POR
 * SEGUNDO no dano de UM contacto, e o resto desta divisão é exatamente o que
 * {@link sawContactDamageMilli} devolve como carry.
 */
export function sawDamageDenominator(level: number): bigint {
  const blades = BigInt(sawBladeCountForLevel(level));
  const speed = BigInt(sawSpeedNumerator(level));
  return blades * speed * MS_PER_SECOND * BPS_ONE;
}

/** O numerador, antes do carry. `base x 240 x nível x 11 600`. */
export function sawDamageNumerator(baseMilli: bigint, level: number): bigint {
  const effective = BigInt(effectiveLevel(level));
  return (
    baseMilli *
    BigInt(SAW_BUDGET_BPS_PER_LEVEL_PER_SECOND) *
    effective *
    BigInt(SAW_CONTACT_INTERVAL_NUMERATOR_MS)
  );
}

/** O que uma instância de dano vale, e o que fica por pagar. */
export interface SawContactDamage {
  /** O dano desta instância, em milli, já inteiro. */
  readonly damageMilli: bigint;
  /** O resto, guardado para o contacto seguinte. Sempre `< denominador`. */
  readonly carry: bigint;
}

/**
 * **O dano de UM contacto, derivado do orçamento total e sem deriva.**
 *
 *                        base x 240 x nível x 11 600  +  carry
 *     dano por contacto = -------------------------------------
 *                          serras x velocidade x 1000 x 10 000
 *
 * ## Porque o orçamento é dividido, e nunca multiplicado
 *
 * Porque a quantidade e a frequência CRESCEM com o nível, e o orçamento não
 * cresce com elas: cresce com o nível e mais nada. Multiplicar `nível x serras x
 * velocidade` daria ao nível 30 quatro serras a cinco contactos por segundo cada
 * uma com o dano de nível 30 - `4 x 2 x 30` vezes o do nível 1, que é 240 vezes
 * um orçamento que só devia ser 30 vezes maior.
 *
 * Então o que o nível compra é o ORÇAMENTO, e as serras e a velocidade são a
 * maneira de o entregar. Quatro serras a bater depressa cada uma por menos.
 *
 * ## O carry, e porque ele não é opcional
 *
 * Porque a divisão trunca, e a fração perdida é a MESMA em todos os contactos -
 * portanto ela não se cancela, acumula. Um casco de nível 9 perde o resto de
 * `25 056/37` milésimos por contacto; ao fim de uma partida de dois minutos são
 * centenas de milésimos que desapareciam em silêncio.
 *
 * Somar o resto ao numerador seguinte faz a taxa de longo prazo ser EXATAMENTE
 * `numerador/denominador` por contacto, seja qual for o horizonte. É a mesma
 * disciplina de `damageCarryMilli` no casco e de `lifestealCarryBps` no sifão: o
 * resto fica guardado, e o golpe seguinte paga-o.
 *
 * O carry pertence ao DONO das serras, e não ao par: é o orçamento dele que está
 * a ser gasto, e um carry por par faria quatro serras contra três inimigos
 * manterem doze restos independentes que nunca somam ao que a nave devia ter
 * feito.
 */
export function sawContactDamageMilli(
  baseMilli: bigint,
  level: number,
  carry: bigint,
): SawContactDamage {
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a saw base is a non-negative bigint, received ${String(baseMilli)}`);
  }
  if (typeof carry !== 'bigint' || carry < 0n) {
    throw new RangeError(`a saw carry is a non-negative bigint, received ${String(carry)}`);
  }
  const denominator = sawDamageDenominator(level);
  if (denominator <= 0n) {
    return { damageMilli: 0n, carry: 0n };
  }
  const total = sawDamageNumerator(baseMilli, level) + carry;
  return { damageMilli: total / denominator, carry: total % denominator };
}

/**
 * O dano por segundo que este casco faz sob contacto sustentado, em milli.
 *
 * Exato e sem o carry: é o que a taxa converge para, que é a grandeza que a
 * tabela do gate publica. `base x 240 x nível / 10 000` por segundo.
 */
export function sawSustainedDamageMilliPerSecond(baseMilli: bigint, level: number): bigint {
  return (
    (baseMilli * BigInt(SAW_BUDGET_BPS_PER_LEVEL_PER_SECOND) * BigInt(effectiveLevel(level))) /
    BPS_ONE
  );
}

/** O que o nível vale em bps de poder de combate. `30 x nível`, e nada acima do 30. */
export function sawBudgetBps(level: number): number {
  return Number(PROC_BUDGET_BPS_PER_LEVEL) * effectiveLevel(level);
}

/* -------------------------------------------------------------------------- */
/*  A órbita                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * **Quantas posições angulares uma órbita tem: 360.**
 *
 * ## Porque a órbita é QUANTIZADA, e não um ângulo contínuo
 *
 * Porque um duelo não tem trigonometria, e a razão está escrita em
 * `pvp/vectors.ts`: `Math.sin` e `Math.cos` são definidos pela implementação no
 * último bit, Node e Chromium já discordam noutro sítio deste repositório, e uma
 * partida é decidida por se uma serra tocou um casco. A correção não é comparar
 * com tolerância - é **nunca produzir o número**.
 *
 * Então a posição de uma serra é um ÍNDICE inteiro numa tabela de direções
 * escritas como literais, e a tabela é a mesma em qualquer motor. Trezentos e
 * sessenta passos porque 360 é divisível por 1, 2, 3 e 4 - as quatro contagens
 * de serras possíveis - o que faz a distribuição uniforme ser exata em vez de
 * arredondada.
 *
 * Um grau por passo. À velocidade máxima - uma volta em 720 ms - são 500 passos
 * por segundo, oito por quadro a 60 Hz: a órbita lê-se contínua.
 */
export const SAW_ORBIT_STEPS = 360;

/**
 * O período de uma volta ao multiplicador 1,00x, em ms.
 *
 * 1 440 ms no nível 1 e 720 ms no nível 30, pelo mesmo multiplicador que decide
 * o intervalo de dano - é isso que o contrato quer dizer com "sem relógios
 * independentes que possam divergir".
 *
 * Escolhido divisível por {@link SAW_ORBIT_STEPS} para que
 * {@link SAW_ORBIT_UNITS_PER_STEP} seja um inteiro exato: 29 é primo e nunca
 * divide 360, portanto o período é que tem de trazer o fator.
 */
export const SAW_ORBIT_BASE_PERIOD_MS = 1_440;

/** Uma volta inteira, em unidades de fase. `período x 29`. */
export const SAW_ORBIT_PHASE_UNITS = SAW_ORBIT_BASE_PERIOD_MS * SAW_SPEED_SCALE;

/** Quantas unidades de fase um passo angular vale. **Derivado**, e exato. */
export const SAW_ORBIT_UNITS_PER_STEP = SAW_ORBIT_PHASE_UNITS / SAW_ORBIT_STEPS;

/**
 * **A fase inicial: zero, para toda a gente.**
 *
 * Determinística e independente da ordem do roster, que é o que o contrato pede.
 * Não é sorteada, não vem do assento, não vem do id do participante e não vem da
 * ordem em que a sala construiu os cascos - todas essas seriam maneiras de duas
 * máquinas discordarem sobre onde uma serra está.
 */
export const SAW_INITIAL_PHASE = 0;

/**
 * A fase deste casco, `deltaMs` depois. Inteira, modular e sem deriva.
 *
 * `fase += dms x velocidade`, e a volta fecha em {@link SAW_ORBIT_PHASE_UNITS}.
 * Os dois modos passam o seu próprio passo - 50 ms no duelo, 16 ms na caça - e
 * chegam à mesma posição ao fim do mesmo tempo simulado, porque a soma é exata.
 *
 * Sem a joia a fase não avança: um casco sem serras não tem órbita, e deixá-la a
 * correr guardaria um número que ninguém lê e que a instantânea teria de levar.
 */
export function sawAdvancePhase(phase: number, deltaMs: number, level: number): number {
  const speed = sawSpeedNumerator(level);
  if (speed <= 0) {
    return SAW_INITIAL_PHASE;
  }
  const advanced = phase + deltaMs * speed;
  return ((advanced % SAW_ORBIT_PHASE_UNITS) + SAW_ORBIT_PHASE_UNITS) % SAW_ORBIT_PHASE_UNITS;
}

/**
 * **O passo angular da serra `index`, de `count` serras.**
 *
 * A fase da nave mais a fatia uniforme desta serra. `360 / count` é exato para
 * as quatro contagens possíveis, portanto nenhuma distribuição é arredondada e
 * quatro serras estão exatamente a 90 graus umas das outras.
 */
export function sawBladeStep(phase: number, index: number, count: number): number {
  if (count <= 0) {
    throw new RangeError(`a saw blade needs at least one blade in the orbit, received ${count}`);
  }
  if (index < 0 || index >= count) {
    throw new RangeError(`saw blade ${index} does not exist in an orbit of ${count}`);
  }
  const base = Math.floor(phase / SAW_ORBIT_UNITS_PER_STEP);
  return (base + index * (SAW_ORBIT_STEPS / count)) % SAW_ORBIT_STEPS;
}

/* -------------------------------------------------------------------------- */
/*  A tabela de direções                                                      */
/* -------------------------------------------------------------------------- */

/**
 * O comprimento de uma direção da tabela. O mesmo `VECTOR_SCALE` do duelo.
 *
 * Repetido aqui em vez de importado de `pvp/vectors.ts` porque este ficheiro é
 * partilhado e não pode conhecer o duelo - a caça importa-o também. A igualdade
 * dos dois números está afirmada em `saw.test.ts`, que é onde conhecer os dois
 * lados é legítimo.
 */
export const SAW_VECTOR_SCALE = 65_536;

/**
 * **O cosseno de 0 a 90 graus, x 65 536, escrito como LITERAIS.**
 *
 * Noventa e um inteiros, e eles são o produto e não uma otimização de um
 * produto. Gerá-los com `Math.cos` ao carregar o módulo devolveria a
 * indeterminação do último bit ao sítio de onde ela foi expulsa: um motor cujo
 * cosseno caísse do outro lado de um arredondamento daria um inteiro diferente,
 * e a serra tocaria o casco numa máquina e falhava noutra.
 *
 * Só um quadrante, e os outros três saem por simetria em {@link sawDirection}.
 * Três quartos da tabela seriam três quartos de oportunidades de um erro de
 * transcrição que nada compararia.
 *
 * `cos(45) = 46 341` é, por construção, o mesmo `VECTOR_DIAGONAL` que o duelo
 * já usa para o movimento em diagonal.
 */
const COSINE_QUADRANT: readonly number[] = Object.freeze([
  65536, 65526, 65496, 65446, 65376, 65287, 65177, 65048, 64898, 64729, 64540, 64332, 64104, 63856,
  63589, 63303, 62997, 62672, 62328, 61966, 61584, 61183, 60764, 60326, 59870, 59396, 58903, 58393,
  57865, 57319, 56756, 56175, 55578, 54963, 54332, 53684, 53020, 52339, 51643, 50931, 50203, 49461,
  48703, 47930, 47143, 46341, 45525, 44695, 43852, 42995, 42126, 41243, 40348, 39441, 38521, 37590,
  36647, 35693, 34729, 33754, 32768, 31772, 30767, 29753, 28729, 27697, 26656, 25607, 24550, 23486,
  22415, 21336, 20252, 19161, 18064, 16962, 15855, 14742, 13626, 12505, 11380, 10252, 9121, 7987,
  6850, 5712, 4572, 3430, 2287, 1144, 0,
]);

/**
 * O cosseno de um passo inteiro, pelas quatro simetrias do quadrante.
 *
 * O `+ 0` nos ramos negativos não é decoração: `-(0)` é `-0` em JavaScript, e
 * `-0` sobrevive a `Object.is`, a um `toEqual` e a um instantâneo. Uma fase que
 * produzisse `-0` num motor e `0` noutro seria uma diferença sem consequência
 * mecânica nenhuma a fazer um digest discordar de si mesmo.
 */
function cosineAt(step: number): number {
  const wrapped = ((step % SAW_ORBIT_STEPS) + SAW_ORBIT_STEPS) % SAW_ORBIT_STEPS;
  if (wrapped <= 90) {
    return COSINE_QUADRANT[wrapped] as number;
  }
  if (wrapped <= 180) {
    return -(COSINE_QUADRANT[180 - wrapped] as number) + 0;
  }
  if (wrapped <= 270) {
    return -(COSINE_QUADRANT[wrapped - 180] as number) + 0;
  }
  return COSINE_QUADRANT[SAW_ORBIT_STEPS - wrapped] as number;
}

/** Uma direção da órbita, em componentes inteiras de comprimento 65 536. */
export interface SawDirection {
  readonly x: number;
  readonly y: number;
}

/**
 * **A direção do passo `step`**, exata e igual em qualquer motor.
 *
 * O seno é o cosseno deslocado um quarto de volta, portanto a tabela de um
 * quadrante chega para os dois eixos e para os quatro quadrantes. A ordem é a do
 * ecrã - `y` cresce para baixo - e é por isso que a órbita corre no sentido dos
 * ponteiros do relógio, que é o sentido em que a arte da joia gira.
 */
export function sawDirection(step: number): SawDirection {
  return { x: cosineAt(step), y: cosineAt(step - 90) };
}

/* -------------------------------------------------------------------------- */
/*  A geometria, em proporção ao casco                                        */
/* -------------------------------------------------------------------------- */

/**
 * O raio da órbita, em basis points do raio do casco. **215 %.**
 *
 * As serras andam do lado de fora e nunca por dentro: a órbita tem de deixar
 * espaço para a lâmina inteira caber fora da silhueta, ou ela desaparece dentro
 * da nave e o jogador vê um efeito que nunca sai. E não tão longe que deixe de
 * se ler como uma coisa que PERTENCE àquela nave.
 *
 * Afastou-se **duas vezes**, as duas por avaliação visual do dono:
 *
 * - **155 % → 185 %** na primeira sessão. A esse raio a lâmina roçava a silhueta
 *   e as duas liam-se como uma massa só;
 * - **185 % → 215 %** na segunda. Com a lâmina já maior - 44 % em vez de 34 % -
 *   os 185 % voltaram a ficar apertados: a folga entre a borda interior e o
 *   casco tinha encolhido em proporção.
 *
 * A 215 % a borda interior fica a 171 % do raio do casco - sete décimos de um
 * raio de folga - e as quatro lâminas passam a desenhar um anel em vez de um
 * contorno colado à nave.
 */
export const SAW_ORBIT_RADIUS_BPS = 21_500;

/**
 * O raio de uma lâmina, em basis points do raio do casco. **44 %.**
 *
 * Uma serra mede cerca de nove décimos do RAIO do casco em diâmetro - grande o
 * suficiente para os sete dentes se lerem ao zoom mínimo, pequena o suficiente
 * para quatro delas não taparem a nave, a barra de vida nem a cor da equipa.
 *
 * Era 34 %, e cresceu por avaliação visual do dono na primeira sessão manual:
 * os dentes existiam e não se liam, o que faz uma serra parecer um disco - que é
 * exatamente o que a arte da joia não é.
 *
 * **É também o raio de COLISÃO**, e é essa a razão de ele viver aqui em vez de
 * numa cena: o contrato exige que a colisão corresponda à serra visível, sem
 * área invisível desproporcionada. Um número na cena e outro na simulação seriam
 * duas verdades sobre a mesma lâmina - e crescer o desenho sem crescer a colisão
 * seria criar exatamente a discrepância que o contrato proíbe, ao contrário.
 */
export const SAW_BLADE_RADIUS_BPS = 4_400;

/** O raio da órbita para um casco deste raio. Inteiro, truncado uma vez. */
export function sawOrbitRadius(shipRadius: number): number {
  return Math.floor((shipRadius * SAW_ORBIT_RADIUS_BPS) / Number(BPS_ONE));
}

/** O raio de uma lâmina para um casco deste raio. O mesmo número que a cena desenha. */
export function sawBladeRadius(shipRadius: number): number {
  return Math.floor((shipRadius * SAW_BLADE_RADIUS_BPS) / Number(BPS_ONE));
}

/* -------------------------------------------------------------------------- */
/*  Os fluxos                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Os rótulos dos fluxos das serras, estáveis e VERSIONADOS.
 *
 * Um fluxo PRÓPRIO em cada modo, e não o da loteria ofensiva, o do veneno ou o
 * do sangramento. Partilhá-lo deslocaria a sequência deles: um casco com o
 * Ciclone e com o Diamante teria a sequência dos tiros mudada pelo número de
 * contactos que as lâminas fizessem - uma quantidade que depende de onde os
 * inimigos andaram.
 *
 * Com um fluxo à parte, uma partida sem esta joia consome exatamente a mesma
 * entropia que consumia antes deste gate. O fluxo nunca chega a ser criado.
 *
 * Versionados porque mudar quantos valores um contacto consome muda toda a
 * sequência a partir daí. Um rótulo novo é a forma de declarar essa mudança em
 * vez de a deixar acontecer em silêncio numa sala já a decorrer.
 */
export const PVP_SAW_RNG_LABEL = 'pvp-jewel-saw-v1';
export const HUNT_SAW_RNG_LABEL = 'hunt-jewel-saw-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_SAW_RNG_LABEL = 'open-world-jewel-saw-v1';

/* -------------------------------------------------------------------------- */
/*  Quem pode ter serras                                                      */
/* -------------------------------------------------------------------------- */

/**
 * **Se esta origem pode manter serras.** Só `cannon`, e a tabela já o diz.
 *
 * Uma serra é da NAVE, e uma nave dispara com o canhão dela. Um pet não tem
 * serras - ele não tem casco, não tem perfil de joias e não é um combatente
 * defensivo - e um efeito de joia não gera serras, que é o mesmo anti-feedback
 * de sempre.
 */
export function sawOriginOwnsBlades(origin: DamageOrigin): boolean {
  return origin === 'cannon';
}

/**
 * O nível deste perfil como `bigint`, para quem precisa dele exato.
 *
 * Existe para as fronteiras que já trabalham em `bigint` não terem de converter
 * duas vezes, e para `toExactBigInt` recusar um `levelSum` malformado no mesmo
 * sítio onde as outras joias o recusam.
 */
export function sawLevelBigInt(profile: JewelSocketProfile): bigint {
  const owned = profile.entries.find((entry) => entry.id === SAW_JEWEL_ID);
  return owned === undefined ? 0n : toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
}

/* -------------------------------------------------------------------------- */
/*  A amostragem em subpassos                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **A fase da órbita no subpasso `step`**, entre a fase anterior e a atual.
 *
 * Uma lâmina que anda depressa atravessa um alvo inteiro dentro de um tique: se
 * o contacto só fosse testado na fase final, ela passaria por cima de uma nave e
 * não lhe tocaria. A resposta é amostrar a volta em subpassos, e esta função é
 * onde o passo se calcula.
 *
 * `phase` é a fase JÁ AVANÇADA deste tique. O avanço bruto é recomputado a
 * partir do nível em vez de guardado, e recuado para descobrir onde a órbita
 * estava: subtrair duas fases já enroladas daria um número negativo na volta que
 * cruzou o zero, e é por isso que a subtração leva o módulo duas vezes.
 *
 * Escrita aqui porque estava escrita três vezes — uma no duelo, uma na caça e
 * uma no mundo aberto — e uma das três dividia com `Math.floor` onde as outras
 * dividiam com truncatura. As duas dão o mesmo para operandos não negativos, que
 * é o único caso que existe; ter as duas escritas era uma divergência à espera
 * de um sinal negativo.
 */
export function sawPhaseAtSubstep(input: {
  /** A fase depois de `sawAdvancePhase` deste tique. */
  readonly phase: number;
  readonly tickMs: number;
  readonly level: number;
  /** 1-based, até `substeps`. */
  readonly step: number;
  readonly substeps: number;
}): number {
  const advance = input.tickMs * sawSpeedNumerator(input.level);
  const previous =
    (((input.phase - advance) % SAW_ORBIT_PHASE_UNITS) + SAW_ORBIT_PHASE_UNITS) %
    SAW_ORBIT_PHASE_UNITS;
  const walked = Math.trunc((advance * input.step) / input.substeps);
  return (previous + walked) % SAW_ORBIT_PHASE_UNITS;
}

/** Onde está o centro de uma lâmina, nesta fase. Inteiro, na unidade do modo. */
export function sawBladeCentre(input: {
  readonly x: number;
  readonly y: number;
  readonly orbitRadius: number;
  readonly phase: number;
  readonly bladeIndex: number;
  readonly bladeCount: number;
}): { readonly x: number; readonly y: number } {
  const direction = sawDirection(sawBladeStep(input.phase, input.bladeIndex, input.bladeCount));
  return {
    x: input.x + Math.trunc((direction.x * input.orbitRadius) / SAW_VECTOR_SCALE),
    y: input.y + Math.trunc((direction.y * input.orbitRadius) / SAW_VECTOR_SCALE),
  };
}
